/**
 * CW-SF-M5 TKT-4 — "Pipeline by Stage" widget.
 *
 * SVG bar chart, inline, no chart library dep. Bars descending by
 * total amount. Amount-denied users see the explanation empty state.
 */
import { useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { formatCurrency, type SfWidgetEnvelope } from './sfWidgetUtils';

interface StageBucket {
  stage: string;
  total_amount: number;
  count: number;
}
interface ApiResponse extends SfWidgetEnvelope {
  items: StageBucket[];
  amount_denied: boolean;
}

const REFRESH_S = 5 * 60;

export function SfPipelineByStageWidget(_props: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>('/mod/cw/widgets/sf/pipeline-by-stage'),
    [],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  const buckets = data?.items ?? [];
  const maxTotal = buckets.length ? Math.max(...buckets.map((b) => b.total_amount)) : 0;

  return (
    <div className="sf-widget sf-widget--pipeline">
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={buckets.length > 0}
        emptyMessage={
          data?.amount_denied
            ? "Your Salesforce profile doesn't have access to Opportunity Amount."
            : 'No open pipeline.'
        }
      >
        <ul className="sf-widget__bars" aria-label="Pipeline by stage">
          {buckets.map((b) => {
            const pct = maxTotal ? (b.total_amount / maxTotal) * 100 : 0;
            return (
              <li key={b.stage} className="sf-widget__bar">
                <div className="sf-widget__bar-label">
                  <span>{b.stage}</span>
                  <span className="sf-widget__bar-meta">
                    {formatCurrency(b.total_amount)} · {b.count}
                  </span>
                </div>
                <svg
                  className="sf-widget__bar-track"
                  viewBox="0 0 100 6"
                  preserveAspectRatio="none"
                  role="img"
                  aria-label={`${b.stage}: ${formatCurrency(b.total_amount)}`}
                >
                  <rect x="0" y="0" width="100" height="6" rx="2" className="sf-widget__bar-bg" />
                  <rect
                    x="0"
                    y="0"
                    width={pct}
                    height="6"
                    rx="2"
                    className="sf-widget__bar-fill"
                  />
                </svg>
              </li>
            );
          })}
        </ul>
      </SfWidgetShell>
    </div>
  );
}
